import { Dialog, DialogContent, DialogTitle, DialogActions, Box, Typography, Button, Chip, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MuiAlert from '@material-ui/lab/Alert';
import api from '../../api/client';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useLocation, useNavigate } from 'react-router-dom';
import { toFarsiDigits, gregorianToJalali } from '../../assets/utils';
import DeleteDepartment from '../../components/admin/DeleteDepartment';
import Departments from '../../components/admin/DepartmentsList';
import EditDepartment from '../../components/admin/EditDepartment';
import AddDepartment from '../../components/admin/AddDepartment';
import Layout from '../../components/layout/Layout';
import { loadingState } from '../../recoil/atoms';
import { departmentsState, usersState } from './atoms';

import UsersList from '../../components/admin/UsersList';
import DeleteUser from '../../components/admin/DeleteUser';
import EditUser from '../../components/admin/EditUser';
import AddUser from '../../components/admin/AddUser';

const ROLE_LB = {
  Admin: 'ادمین',
  Department: 'اداره',
  DepartmentManager: 'مدیر سازمان',
  Contractor: 'پیمانکار',
  Consultant: 'مشاور',
  Supervisor: 'ناظر',
};
const ROLE_ST = {
  Admin: { background: '#e8eafd', color: '#1a237e' },
  Department: { background: '#e3f2fd', color: '#1565c0' },
  DepartmentManager: { background: '#e0f2f1', color: '#00695c' },
  Contractor: { background: '#f3e5f5', color: '#7b1fa2' },
  Consultant: { background: '#fff3e0', color: '#ef6c00' },
  Supervisor: { background: '#e0f7fa', color: '#00838f' },
};

// آمارِ «قرارداد / حواله / اشتراک» فقط برای پیمانکار معنادار است.
// سازمان، مدیر سازمان، ناظر، مشاور و ادمین هیچ قرارداد، حواله یا اشتراکِ خود ندارند
// ⇒ این چیپ‌ها برای آن‌ها نمایش داده نمی‌شود.
const isContractorRole = (role) => role === 'Contractor';

// ── گروه‌های «کاربران آنلاین» در داشبورد مدیر ──
// هر کاربر در اولین گروهی که با نقشش (اصلی یا نقش دوم) می‌خواند قرار می‌گیرد
const ONLINE_GROUPS = [
  { key: 'dept_managers', title: 'مدیران سازمان', icon: '🏢', roles: ['DepartmentManager'],
    bg: '#e0f2f1', fg: '#00695c', border: '#b2dfdb' },
  { key: 'supervisors', title: 'ناظران', icon: '🔍', roles: ['Supervisor'],
    bg: '#e0f7fa', fg: '#00838f', border: '#b2ebf2' },
  { key: 'dept_users', title: 'کاربران سازمان', icon: '🏛️', roles: ['Department'],
    bg: '#e3f2fd', fg: '#1565c0', border: '#bbdefb' },
];


// پالتِ کارت‌های آماری — شاد و هم‌خانواده (۳ سطر × ۵)
const STAT_GRADS = [
  // سطر ۱ — سازمان/قرارداد (فیروزه‌ای و آبی روشن)
  ['linear-gradient(135deg,#0891b2,#22d3ee)', '0 6px 18px rgba(8,145,178,.28)'],
  ['linear-gradient(135deg,#06b6d4,#67e8f9)', '0 6px 18px rgba(6,182,212,.28)'],
  ['linear-gradient(135deg,#0284c7,#38bdf8)', '0 6px 18px rgba(2,132,199,.28)'],
  ['linear-gradient(135deg,#0d9488,#2dd4bf)', '0 6px 18px rgba(13,148,136,.28)'],
  ['linear-gradient(135deg,#0f766e,#5eead4)', '0 6px 18px rgba(15,118,110,.28)'],
  // سطر ۲ — کاربران/اشتراک (آبی/نیلی/بنفش)
  ['linear-gradient(135deg,#2563eb,#60a5fa)', '0 6px 18px rgba(37,99,235,.28)'],
  ['linear-gradient(135deg,#4338ca,#818cf8)', '0 6px 18px rgba(67,56,202,.28)'],
  ['linear-gradient(135deg,#4f46e5,#a5b4fc)', '0 6px 18px rgba(79,70,229,.28)'],
  ['linear-gradient(135deg,#6d28d9,#a78bfa)', '0 6px 18px rgba(109,40,217,.28)'],
  ['linear-gradient(135deg,#7c3aed,#c084fc)', '0 6px 18px rgba(124,58,237,.28)'],
  // سطر ۳ — بازدید/پیام (سبز و صورتی)
  ['linear-gradient(135deg,#059669,#34d399)', '0 6px 18px rgba(5,150,105,.28)'],
  ['linear-gradient(135deg,#10b981,#6ee7b7)', '0 6px 18px rgba(16,185,129,.28)'],
  ['linear-gradient(135deg,#15803d,#4ade80)', '0 6px 18px rgba(21,128,61,.28)'],
  ['linear-gradient(135deg,#16a34a,#86efac)', '0 6px 18px rgba(22,163,74,.28)'],
  ['linear-gradient(135deg,#db2777,#f9a8d4)', '0 6px 18px rgba(219,39,119,.28)'],
];

const useStyles = makeStyles(() => ({
  orgGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(255px,1fr))', gap: 12,
  },
  orgCard: {
    background: '#fff', border: '1px solid #e6eef2', borderRadius: 14, padding: '14px 16px',
    boxShadow: '0 2px 10px rgba(15,40,60,.05)', cursor: 'pointer',
    transition: 'transform .15s, box-shadow .15s, border-color .15s',
    '&:hover': {
      borderColor: '#0f6e7d', boxShadow: '0 6px 18px rgba(15,110,125,.15)', transform: 'translateY(-2px)',
    },
  },
  orgTitle: { fontSize: 13.5, fontWeight: 800, color: '#0f172a', lineHeight: 1.6, marginBottom: 9 },
  orgNote: { fontSize: 11.5, color: '#64748b', marginTop: 8, fontWeight: 700 },
  pills: { display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' },
  pillNormal: {
    borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    background: '#e0f2fe', color: '#075985', border: '1px solid #7dd3fc',
  },
  pillSpecial: {
    borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
  },
  pillTotal: {
    borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
    gap: 12,
    marginBottom: 20,
    '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    '@media (max-width: 700px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  statCard: {
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: 'linear-gradient(135deg, rgba(255,255,255,.16), transparent 60%)',
    },
    '&:hover': { transform: 'translateY(-2px)', filter: 'brightness(1.06)' },
  },
}));

const AdminDashboard = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const setLoading = useSetRecoilState(loadingState);
  const setDepartments = useSetRecoilState(departmentsState);
  const departments = useRecoilValue(departmentsState);
  const setUsers = useSetRecoilState(usersState);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userStats, setUserStats] = useState({});
  const [onlineDlg, setOnlineDlg] = useState(false);
  const [onlineRows, setOnlineRows] = useState([]);
  // کدام بخشِ «کاربران آنلاین» باز است (کلیدِ گروه)؛ خالی = همه بسته
  const [openGroup, setOpenGroup] = useState('');
  const [expDlg, setExpDlg] = useState(false);
  const [expRows, setExpRows] = useState([]);
  const [smsResult, setSmsResult] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  // هشدار پرداخت‌های تأییدشده در درگاه که اشتراک‌شان ثبت نشده
  const [payAlerts, setPayAlerts] = useState({ count: 0, items: [] });
  const [payDlg, setPayDlg] = useState(false);
  // آمار حوالهٔ پیمانکارانِ بی‌مشترک (۳ ماه گذشته)
  const [cStats, setCStats] = useState(null);
  // آخرین به‌روزرسانیِ خودکارِ داشبورد (برای نمایشِ «به‌روزرسانی خودکار»)
  const [lastRefresh, setLastRefresh] = useState(null);
  const lastRefreshRef = useRef(null);


  // ── گروه‌بندیِ کاربران آنلاین بر اساس نقش (بخش‌های بازشوندهٔ داشبورد) ──
  const onlineGroups = useMemo(() => {
    const groups = ONLINE_GROUPS.map((g) => ({ ...g, rows: [] }));
    (onlineRows || []).forEach((u) => {
      const roles = [u.role, u.role2].filter(Boolean);
      for (const g of groups) {
        if (g.roles.some((r) => roles.indexOf(r) >= 0)) {
          g.rows.push(u);
          return;
        }
      }
    });
    return groups.filter((g) => g.rows.length > 0);
  }, [onlineRows]);

  const openOnline = async () => {
    setOnlineDlg(true);
    try {
      const r = await api.get('users/online/');
      setOnlineRows(r.data || []);
    } catch (e) { /* خطا در همان دیالوگ نمایش داده می‌شود */ }
  };

  const openExpiring = async () => {
    setExpDlg(true);
    setExpRows([]);
    setSmsResult(null);
    try {
      const r = await api.get('users/premium-expiring/');
      setExpRows(r.data || []);
    } catch (e) { /* خطا در همان دیالوگ نمایش داده می‌شود */ }
  };

  const [visDlg, setVisDlg] = useState(false);
  const [visRows, setVisRows] = useState([]);
  const [visTitle, setVisTitle] = useState('');
  const [visErr, setVisErr] = useState('');

  const openVisitors = async (scope) => {
    setVisDlg(true);
    setVisRows([]);
    setVisErr('');
    try {
      const r = await api.get(`users/visitors/?scope=${scope}`);
      setVisRows(r.data.items || []);
      setVisTitle(r.data.title || '');
    } catch (e) {
      setVisErr(
        (e && e.response && e.response.data && e.response.data.detail)
        || 'دریافت فهرست بازدیدکنندگان ناموفق بود. دوباره تلاش کنید.'
      );
    }
  };

  const [msgDlg, setMsgDlg] = useState(false);
  const [msgScope, setMsgScope] = useState('inbox');
  const [msgRows, setMsgRows] = useState([]);
  const [msgText, setMsgText] = useState({});
  const [msgBusy, setMsgBusy] = useState(false);

  const openMsgs = async (scope) => {
    const sc = scope || msgScope;
    setMsgScope(sc);
    setMsgDlg(true);
    try {
      const url = sc === 'oversight'
        ? 'messages/admin/oversight/'
        : 'messages/admin/inbox/';
      const r = await api.get(url);
      setMsgRows(r.data || []);
    } catch (e) { /* در دیالوگ نمایش داده می‌شود */ }
  };

  const sendReply = async (row) => {
    const text = (msgText[row.other_user_id] || '').trim();
    if (!text) return;
    setMsgBusy(true);
    try {
      await api.post('messages/', {
        contract_id: row.contract_id || '',
        subject: row.subject,
        receiver_id: row.other_user_id,
        message: text,
      });
      setMsgText((m) => ({ ...m, [row.other_user_id]: '' }));
      openMsgs('inbox');
    } catch (e) {
      alert('ارسال پاسخ ناموفق بود');
    } finally {
      setMsgBusy(false);
    }
  };

  const sendExpirySms = async () => {
    setSmsSending(true);
    setSmsResult(null);
    try {
      const r = await api.post('users/premium-expiring/');
      setSmsResult(r.data);
    } catch (e) {
      setSmsResult({ sent: -1 });
    } finally {
      setSmsSending(false);
    }
  };

  const [openEditDepartment, setOpenEditDepartment] = useState(-1);
  const [openDeleteDepartment, setOpenDeleteDepartment] = useState(-1);
  const [openAddDepartment, setOpenAddDepartment] = useState(false);

  const [openEditUser, setOpenEditUser] = useState(-1);
  const [openDeleteUser, setOpenDeleteUser] = useState(-1);
  const [openAddUser, setOpenAddUser] = useState(false);

  // میان‌بر: /users?new=1 ⇒ باز کردنِ مستقیمِ دیالوگ «افزودن کاربر»
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      if (params.get('new') === '1') {
        setOpenAddUser(true);
        navigate('/users', { replace: true });
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [ordering, setOrdering] = useState('last_upload');
  const [refreshTick, setRefreshTick] = useState(0);

  // بهروزشدن لیست بعد از توقف پردازش (رویداد از DepartmentsList)
  useEffect(() => {
    const onRefresh = () => setRefreshTick((t) => t + 1);
    window.addEventListener('departments-refresh', onRefresh);
    return () => window.removeEventListener('departments-refresh', onRefresh);
  }, []);

  const fetchDepartments = useCallback((orderParam) => {
    return api.get(`users/departments/?ordering=${orderParam}`);
  }, []);

  // بارگذاریِ داده‌های داشبورد (سازمان‌ها + کاربران + آمار)
  const loadDashboard = useCallback((withSpinner) => {
    if (withSpinner) setLoading(true);
    return Promise.all([
      fetchDepartments(ordering),
      api.get('users/admin/users/'),
      api.get('users/online/'),
    ])
      .then(([deps, users, online]) => {
        setDepartments(deps.data);
        setUsers((users.data && users.data.users) ? users.data.users : []);
        setUsersTotal((users.data && users.data.summary && users.data.summary.count) || 0);
        setUserStats((users.data && users.data.summary) || {});
        setOnlineRows((online && online.data) || []);
        const now = new Date();
        setLastRefresh(now);
        lastRefreshRef.current = now;
      })
      .catch(() => { /* خطا در همان صفحه نمایش داده می‌شود */ })
      .finally(() => { if (withSpinner) setLoading(false); });
  }, [setDepartments, setUsers, setUsersTotal, setUserStats, setLoading, ordering, fetchDepartments]);

  useEffect(() => {
    loadDashboard(true);
  }, [loadDashboard, refreshTick]);

  // ── به‌روزرسانیِ خودکارِ داشبورد (هر ۶۰ ثانیه، بدون رفرشِ صفحه) ──
  const refreshNow = useCallback(() => {
    if (document.hidden) return;          // تب غیرفعال ⇒ بعداً به‌روز می‌شود
    if (openAddUser || openEditUser !== -1 || openDeleteUser !== -1) return;
    loadDashboard(false);
    api.get('users/payment-alerts/')
      .then((r) => setPayAlerts(r.data || { count: 0, items: [] }))
      .catch(() => {});
    api.get('messages/admin/inbox/')
      .then((r) => setMsgRows(r.data || []))
      .catch(() => { });
    if (location.pathname === '/') {
      api.get('users/admin/contractor-stats/?days=90')
        .then((r) => setCStats(r.data || null))
        .catch(() => { });
    }
  }, [loadDashboard, location.pathname, openAddUser, openEditUser, openDeleteUser]);

  useEffect(() => {
    const t = setInterval(refreshNow, 60000);
    return () => clearInterval(t);
  }, [refreshNow]);

  // به‌محض برگشتنِ کاربر به تب ⇒ اگر داده کهنه بود، فوراً تازه کن
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) return;
      const lr = lastRefreshRef.current;
      if (!lr || (Date.now() - lr.getTime()) > 60000) refreshNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshNow]);

  useEffect(() => {
    api.get('users/payment-alerts/')
      .then((r) => setPayAlerts(r.data || { count: 0, items: [] }))
      .catch(() => setPayAlerts({ count: 0, items: [] }));
    // صندوق پیام مدیریت (برای شمارش نخوانده‌ها روی کارت)
    api.get('messages/admin/inbox/')
      .then((r) => setMsgRows(r.data || []))
      .catch(() => { });
  }, []);

  // آمار حوالهٔ پیمانکارانِ بی‌مشترک — فقط روی صفحهٔ داشبورد
  useEffect(() => {
    if (location.pathname !== '/') return;
    api.get('users/admin/contractor-stats/?days=90')
      .then((r) => setCStats(r.data || null))
      .catch(() => setCStats(null));
  }, [location.pathname]);

  const handleOrderingChange = (newOrdering) => {
    setOrdering(newOrdering);
  };

  const closeDialog = () => {
    setOpenDeleteDepartment(-1);
    setOpenEditDepartment(-1);
    setOpenAddDepartment(false);
    setOpenDeleteUser(-1);
    setOpenEditUser(-1);
    setOpenAddUser(false);
  };

  const visits14 = Array.isArray(userStats.visits_14d) ? userStats.visits_14d : [];
  const todayVisit = visits14.find((r) => r.is_today) || { visits: 0, users: 0 };

  const cStatsDepts = (cStats && Array.isArray(cStats.departments)) ? cStats.departments : [];

  return (
    <>
      <Layout>
        {/* صفحهٔ اصلی: فقط داشبورد (کارتها) + کیوآرکد */}
        {location.pathname === '/' && (
          <>
        {/* هشدار پرداخت‌های ثبت‌نشده */}
        {payAlerts.count > 0 && (
          <MuiAlert
            severity="error"
            style={{ marginBottom: 16, direction: 'rtl', cursor: 'pointer', fontWeight: 700 }}
            onClick={() => setPayDlg(true)}
          >
            ⚠️ {toFarsiDigits(payAlerts.count)} پرداخت در درگاه تأیید شده ولی اشتراک آن ثبت نشده است
            — برای مشاهدهٔ فهرست کلیک کنید
          </MuiAlert>
        )}

        {/* کارت‌های آماری */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap"
          style={{ marginBottom: 8, gap: 8 }}>
          <Typography variant="caption" style={{ color: '#7a80a3', fontWeight: 700 }}>
            🔄 داشبورد هر ۶۰ ثانیه به‌صورت خودکار به‌روز می‌شود
            {lastRefresh ? ` — آخرین به‌روزرسانی: ${toFarsiDigits(lastRefresh.toLocaleTimeString('fa-IR'))}` : ''}
          </Typography>
        </Box>
        <Box className={classes.cardsGrid} data-tour="dashboard-cards">
          {[
            { icon: '🏛️', num: toFarsiDigits(departments.length), lbl: 'کل سازمان‌ها', bg: '#eef0fd', fg: '#3949ab',
              accent: '#5c6bc0', },
            {
              icon: '⚙️',
              num: toFarsiDigits(departments.filter((d) => d.is_processing).length),
              lbl: 'در حال پردازش بارگذاری',
              bg: '#fff4e5',
              fg: '#e65100',
              accent: '#f57c00',
            },
            {
              icon: '📄',
              num: toFarsiDigits(userStats.contracts_total || 0),
              lbl: 'کل قراردادها',
              bg: '#e0f7fa', fg: '#00838f', accent: '#00bcd4',
            },
            {
              icon: '🏗️',
              num: toFarsiDigits(userStats.contractors_total || 0),
              lbl: 'پیمانکاران',
              bg: '#e0f7fa', fg: '#00838f', accent: '#00bcd4',
            },
            { icon: '👥', num: toFarsiDigits(usersTotal.toLocaleString('fa-IR')), lbl: 'کل کاربران', bg: '#e3f2fd', fg: '#1565c0',
              accent: '#1e88e5', },
            {
              icon: '💳',
              num: toFarsiDigits(userStats.subs_today || 0),
              lbl: 'اشتراک امروز',
              bg: '#e3f2fd',
              fg: '#1565c0',
              accent: '#1e88e5',
            },
            {
              icon: '📆',
              num: toFarsiDigits(userStats.subs_month || 0),
              lbl: 'اشتراک این ماه',
              bg: '#e8eafd',
              fg: '#3949ab',
              accent: '#5c6bc0',
            },
            {
              icon: '⭐',
              num: toFarsiDigits(userStats.premium_active || 0),
              lbl: 'کاربران ویژهٔ فعال (پرداخت ۱ سال اخیر)',
              bg: '#fff8e1',
              fg: '#f9a825',
              accent: '#fbc02d',
            },
            {
              icon: '⏳',
              num: toFarsiDigits(userStats.premium_expiring_month || 0),
              lbl: `انقضای ویژه در ${userStats.premium_expiring_month_fa || 'ماه جاری'}`,
              bg: '#fbe9e7',
              fg: '#d84315',
              accent: '#f4511e',
              onClick: openExpiring,
            },
            {
              icon: '👀',
              num: toFarsiDigits(userStats.visited_2m || 0),
              lbl: 'بازدید در ۲ ماه اخیر',
              bg: '#f3e5f5',
              fg: '#6a1b9a',
              accent: '#8e24aa',
            },
            {
              icon: '🟢',
              num: toFarsiDigits(userStats.online || 0),
              lbl: 'کاربران آنلاین (۵ دقیقه اخیر)',
              bg: '#e8f5e9',
              fg: '#2e7d32',
              accent: '#43a047',
              onClick: openOnline,
            },
            {
              icon: '📅',
              num: toFarsiDigits(userStats.visitors_today || 0),
              lbl: 'بازدید امروز',
              bg: '#e1f5fe',
              fg: '#01579b',
              accent: '#0288d1',
              onClick: () => openVisitors('today'),
            },
            {
              icon: '🗓',
              num: toFarsiDigits(userStats.visitors_month || 0),
              lbl: 'بازدید این ماه',
              bg: '#eceff1',
              fg: '#37474f',
              accent: '#546e7a',
              onClick: () => openVisitors('month'),
            },
            {
              icon: '💬',
              num: toFarsiDigits(msgRows.reduce((s, r) => s + (r.unread_count || 0), 0)),
              lbl: 'پیام‌های نخواندهٔ مدیریت',
              bg: '#fce4ec',
              fg: '#ad1457',
              accent: '#d81b60',
              onClick: () => openMsgs('inbox'),
            },
          ].map((k, i) => (
            <Box
              key={i}
              onClick={k.onClick}
              className={classes.statCard}
              style={{
                background: STAT_GRADS[i % STAT_GRADS.length][0],
                boxShadow: STAT_GRADS[i % STAT_GRADS.length][1],
                borderRadius: 14,
                padding: '16px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: k.onClick ? 'pointer' : 'default',
                transition: 'transform .15s, filter .15s, box-shadow .15s',
              }}
            >
              <Box
                style={{
                  position: 'relative',
                  zIndex: 1,
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: 'rgba(255,255,255,.18)',
                  border: '1px solid rgba(255,255,255,.38)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                {k.icon}
              </Box>
              <Box style={{ position: 'relative', zIndex: 1, minWidth: 0 }}>
                <Typography style={{ fontWeight: 800, fontSize: 24, lineHeight: 1.2, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{k.num}</Typography>
                <Typography style={{ fontSize: 12, color: 'rgba(255,255,255,.9)', fontWeight: 600, marginTop: 2 }}>{k.lbl}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── کاربران آنلاین: سه بخشِ بازشونده (مدیران سازمان / ناظران / کاربران سازمان) ── */}
        {onlineGroups.length > 0 && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {onlineGroups.map((g) => {
              const isOpen = openGroup === g.key;
              return (
                <Box key={g.key} style={{
                  background: '#fff', borderRadius: 14, border: '1px solid #ecedf6',
                  boxShadow: '0 3px 14px rgba(20,30,80,.05)', overflow: 'hidden',
                }}>
                  <Box
                    onClick={() => setOpenGroup(isOpen ? '' : g.key)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 18px', cursor: 'pointer', flexWrap: 'wrap', gap: 8,
                    }}
                  >
                    <Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 19 }}>{g.icon}</span>
                      <Typography style={{ fontWeight: 800, fontSize: 14.5 }}>{g.title}</Typography>
                      <Chip size="small" label={toFarsiDigits(g.rows.length)} style={{
                        fontWeight: 800, background: g.bg, color: g.fg, border: '1px solid ' + g.border,
                      }} />
                      <Typography style={{ fontSize: 11.5, color: '#7a80a3' }}>آنلاین (۵ دقیقه اخیر)</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography style={{ fontSize: 11.5, color: '#0f6e7d', fontWeight: 700 }}>
                        {isOpen ? 'بستن' : 'نمایش فهرست'}
                      </Typography>
                      <span style={{ fontSize: 12, color: '#7a80a3' }}>{isOpen ? '▲' : '▼'}</span>
                    </Box>
                  </Box>
                  {isOpen && (
                    <Box style={{ borderTop: '1px solid #f1f2f8', padding: '4px 18px 10px' }}>
                      {g.rows.map((u) => (
                        <Box key={u.id} display="flex" justifyContent="space-between" alignItems="center"
                          style={{ padding: '7px 0', borderBottom: '1px solid #f7f8fc', flexWrap: 'wrap', gap: 8 }}>
                          <Box style={{ minWidth: 150 }}>
                            <Typography variant="body2" style={{ fontWeight: 700 }}>{u.name || u.username}</Typography>
                            <Typography variant="caption" style={{ fontFamily: 'monospace', direction: 'ltr', color: '#9aa1b9' }}>
                              {u.username}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                            {u.last_seen_fa && (
                              <Typography variant="caption" style={{ color: '#7a80a3' }}>
                                آخرین فعالیت: {toFarsiDigits(u.last_seen_fa)}
                              </Typography>
                            )}
                            <Chip size="small" label={ROLE_LB[u.role] || u.role}
                              style={{ fontWeight: 700, ...(ROLE_ST[u.role] || { background: '#eceff1', color: '#455a64' }) }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* نمودار آمار بازدید روزانه (۱۴ روز گذشته) — مثل داشبورد فیش دستمزد */}
        {visits14.length > 0 && (
          <Box style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecedf6',
            boxShadow: '0 3px 14px rgba(20,30,80,.05)',
            padding: '16px 18px',
            marginBottom: 20,
          }}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between"
              flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
              <Typography style={{ fontWeight: 800, fontSize: 15 }}>📊 آمار بازدید روزانه</Typography>
              <Typography style={{ fontSize: 11.5, color: '#7a80a3' }}>
                ۱۴ روز گذشته — امروز: {toFarsiDigits(todayVisit.visits)} بازدید · {toFarsiDigits(todayVisit.users)} کاربر
              </Typography>
            </Box>
            {visits14.map((r, i) => (
              <Box key={i} display="flex" alignItems="center" style={{ gap: 10, marginBottom: 6 }}>
                <span style={{
                  flex: '0 0 86px',
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                  color: r.is_today ? '#0b3d4f' : '#475569',
                  fontWeight: r.is_today ? 800 : 500,
                }}>
                  {toFarsiDigits(r.label)}
                </span>
                <span style={{ flex: '1 1 auto', height: 14, background: '#eef3f6', borderRadius: 7, overflow: 'hidden', display: 'block' }}>
                  <span style={{
                    display: 'block',
                    height: '100%',
                    width: r.pct + '%',
                    minWidth: 2,
                    borderRadius: 7,
                    background: r.is_today
                      ? 'linear-gradient(90deg,#059669,#10b981)'
                      : 'linear-gradient(90deg,#0f6e7d,#0ea5e9)',
                  }} />
                </span>
                <span style={{ flex: '0 0 52px', textAlign: 'left', fontSize: 12.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                  {toFarsiDigits(r.visits)}
                </span>
                <span style={{ flex: '0 0 92px', fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {toFarsiDigits(r.users)} کاربر
                </span>
              </Box>
            ))}
          </Box>
        )}

        {/* حواله‌های ۳ ماه گذشتهٔ پیمانکارانِ بی‌مشترک — به تفکیک سازمان */}
        {cStats && cStatsDepts.length > 0 && (
          <Box style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecedf6',
            boxShadow: '0 3px 14px rgba(20,30,80,.05)',
            padding: '16px 18px',
            marginBottom: 20,
          }}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between"
              flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
              <Typography style={{ fontWeight: 800, fontSize: 15 }}>
                💰 حوالهٔ پیمانکاران
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Typography style={{ fontSize: 11.5, color: '#7a80a3' }}>
                  {cStats.since} تا {cStats.today} — {toFarsiDigits(cStats.total_transfers || 0)} حواله · ⭐{' '}
                  {toFarsiDigits(cStats.sub_count || 0)} پیمانکارِ مشترک ·{' '}
                  {toFarsiDigits(cStats.unsub_count || 0)} غیر مشترک
                </Typography>
                <Button size="small" variant="outlined" onClick={() => navigate('/contractor-stats')}>
                  فهرست کامل ›
                </Button>
              </Box>
            </Box>
            <Box className={classes.orgGrid}>
            {cStatsDepts.map((d) => (
              <div
                key={d.id}
                className={classes.orgCard}
                onClick={() => navigate(`/contractor-stats?dept=${d.id}`)}
                title={`نمایش پیمانکاران بی‌مشترک ${d.name}`}
              >
                <div className={classes.orgTitle}>{d.name}</div>
                <div className={classes.pills}>
                  <span className={classes.pillNormal}>حواله {toFarsiDigits(d.n)}</span>
                  {d.sub_contractors > 0 ? (
                    <span className={classes.pillSpecial}>⭐ مشترک {toFarsiDigits(d.sub_contractors)}</span>
                  ) : null}
                  <span className={classes.pillTotal}>غیر مشترک {toFarsiDigits(d.unsub_contractors)}</span>
                </div>
                <div className={classes.orgNote}>
                  {toFarsiDigits(d.sub_transfers)} حوالهٔ مشترک · {toFarsiDigits(d.unsub_transfers)} حوالهٔ غیر مشترک
                </div>
              </div>
            ))}
            </Box>
          </Box>
        )}

        {/* وضعیت سرور: فضای دیسک و پایگاه داده — فقط مدیر */}
        {userStats.sys && userStats.sys.disk_total ? (
          <Box style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #ecedf6',
            boxShadow: '0 3px 14px rgba(20,30,80,.05)',
            padding: '16px 18px',
            marginBottom: 20,
          }}>
            <Typography style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>🖥️ وضعیت سرور</Typography>
            <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 10 }}>
              <span style={{ flex: '0 0 96px', fontSize: 12.5, fontWeight: 700, color: '#475569' }}>فضای دیسک</span>
              <span style={{ flex: '1 1 auto', height: 14, background: '#eef3f6', borderRadius: 7, overflow: 'hidden', display: 'block' }}>
                <span style={{
                  display: 'block',
                  height: '100%',
                  minWidth: 2,
                  borderRadius: 7,
                  width: userStats.sys.disk_pct + '%',
                  background: userStats.sys.disk_pct >= 90
                    ? 'linear-gradient(90deg,#b91c1c,#ef4444)'
                    : userStats.sys.disk_pct >= 75
                      ? 'linear-gradient(90deg,#b45309,#f59e0b)'
                      : 'linear-gradient(90deg,#0f6e7d,#0ea5e9)',
                }} />
              </span>
              <span style={{ flex: '0 0 54px', textAlign: 'left', fontSize: 12.5, fontWeight: 800 }}>{toFarsiDigits(userStats.sys.disk_pct)}٪</span>
              <span style={{ flex: '0 0 224px', fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {toFarsiDigits(userStats.sys.disk_used_gb)} از {toFarsiDigits(userStats.sys.disk_total_gb)} گیگابایت · {toFarsiDigits(userStats.sys.disk_free_gb)} گیگابایت آزاد
              </span>
            </Box>
            <Box display="flex" flexWrap="wrap" style={{ gap: '8px 22px', fontSize: 12.5, color: '#334155' }}>
              {userStats.sys.db_size ? (
                <span>پایگاه داده: <b style={{ fontWeight: 800, color: '#0f172a' }}>{userStats.sys.db_size}</b> ({toFarsiDigits(userStats.sys.db_mb)} مگابایت)</span>
              ) : null}
              {userStats.sys.db_tables ? (
                <span>جدول‌ها: <b style={{ fontWeight: 800, color: '#0f172a' }}>{toFarsiDigits(userStats.sys.db_tables)}</b></span>
              ) : null}
              {userStats.sys.db_version ? (
                <span>PostgreSQL: <b style={{ fontWeight: 800, color: '#0f172a' }}>{userStats.sys.db_version}</b></span>
              ) : null}
              <span>کل دیسک: <b style={{ fontWeight: 800, color: '#0f172a' }}>{toFarsiDigits(userStats.sys.disk_total_gb)} گیگابایت</b></span>
            </Box>
          </Box>
        ) : null}
          </>
        )}

        {location.pathname === '/organizations' && (
          <Departments
            setOpenEditDepartment={setOpenEditDepartment}
            setOpenDeleteDepartment={setOpenDeleteDepartment}
            setOpenAddDepartment={setOpenAddDepartment}
            ordering={ordering}
            onOrderingChange={handleOrderingChange}
          />
        )}

        {location.pathname === '/users' && (
          <UsersList
            setOpenEditUser={setOpenEditUser}
            setOpenDeleteUser={setOpenDeleteUser}
            setOpenAddUser={setOpenAddUser}
          />
        )}
      </Layout>

      <Dialog
        style={{ direction: 'rtl' }}
        fullWidth
        open={
          (openDeleteDepartment !== -1) ||
          (openEditDepartment !== -1) ||
          (openAddDepartment) ||
          (openDeleteUser !== -1) ||
          (openEditUser !== -1) ||
          (openAddUser)
        }
        onClose={closeDialog}
        maxWidth='sm'
      >
      {openDeleteDepartment !== -1 && (
          <>
            <DialogTitle onClose={closeDialog} style={{ background: '#ffebee', color: '#c62828', fontWeight: 800 }}>
              ⚠ حذف اداره
            </DialogTitle>
            <DialogContent>
              <DeleteDepartment id={openDeleteDepartment} setOpenDeleteDepartment={setOpenDeleteDepartment} />
            </DialogContent>
          </>
        )}

        {openEditDepartment !== -1 && (
          <>
            <DialogTitle onClose={closeDialog}>ویرایش اداره</DialogTitle>
            <DialogContent>
              <EditDepartment id={openEditDepartment} setOpenEditDepartment={setOpenEditDepartment} />
            </DialogContent>
          </>
        )}

        {openAddDepartment && (
          <>
            <DialogTitle onClose={closeDialog}>افزودن سازمان جدید</DialogTitle>
            <DialogContent>
              <AddDepartment setOpenAddDepartment={setOpenAddDepartment} />
            </DialogContent>
          </>
        )}

        {openDeleteUser !== -1 && (
          <>
            <DialogTitle onClose={closeDialog} style={{ background: '#ffebee', color: '#c62828', fontWeight: 800 }}>
              ⚠ حذف کاربر
            </DialogTitle>
            <DialogContent>
              <DeleteUser id={openDeleteUser} setOpenDeleteUser={setOpenDeleteUser} />
            </DialogContent>
          </>
        )}

        {openEditUser !== -1 && (
          <>
            <DialogTitle onClose={closeDialog}>ویرایش کاربر</DialogTitle>
            <DialogContent>
              <EditUser id={openEditUser} setOpenEditUser={setOpenEditUser} />
            </DialogContent>
          </>
        )}

        {openAddUser && (
          <>
            <DialogTitle onClose={closeDialog}>افزودن کاربر جدید</DialogTitle>
            <DialogContent>
              <AddUser setOpenAddUser={setOpenAddUser} />
            </DialogContent>
          </>
        )}
      </Dialog>

        {/* دیالوگ کاربران آنلاین */}
      <Dialog open={onlineDlg} onClose={() => setOnlineDlg(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ fontWeight: 'bold' }}>🟢 کاربران آنلاین (۵ دقیقه اخیر)</DialogTitle>
        <DialogContent>
          {onlineRows.length === 0 && (
            <Typography variant="body2" color="textSecondary">در حال حاضر کاربری آنلاین نیست.</Typography>
          )}
          {onlineRows.map((u) => (
            <Box key={u.id} display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '7px 0', borderBottom: '1px solid #f0f1f8', flexWrap: 'wrap', gap: 8 }}>
              <Box style={{ minWidth: 140 }}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>
                  {u.name || u.username}
                </Typography>
                <Typography variant="caption" style={{ fontFamily: 'monospace', direction: 'ltr', color: '#9aa1b9' }}>
                  {u.username}
                </Typography>
                {u.last_seen_fa && (
                  <Typography variant="caption" style={{ display: 'block', color: '#7a80a3' }}>
                    آخرین فعالیت: {toFarsiDigits(u.last_seen_fa)}
                  </Typography>
                )}
              </Box>
              {isContractorRole(u.role) && (
                <>
                  <Chip size="small" label={`${toFarsiDigits(u.contracts_count ?? 0)} قرارداد`}
                    style={{ minWidth: 92, fontWeight: 700, background: '#e8eaf6', color: '#283593' }} />
                  <Chip size="small" label={`${toFarsiDigits(u.transfers_year ?? 0)} حواله در یکسال گذشته`}
                    style={{ minWidth: 172, fontWeight: 700, background: '#e0f2f1', color: '#00695c' }} />
                </>
              )}
              <Box display="flex" gap={0.5} alignItems="center">
                {isContractorRole(u.role) && (
                  <Chip
                    size="small"
                    label={u.subscribed
                      ? `⭐ ویژه${u.sub_days_left != null ? ` · ${toFarsiDigits(u.sub_days_left)} روز` : ''}`
                      : 'بدون اشتراک'}
                    style={{
                      fontWeight: 700,
                      ...(u.subscribed
                        ? { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }
                        : { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }),
                    }}
                  />
                )}
                <Chip
                  size="small"
                  label={ROLE_LB[u.role] || u.role}
                  style={{ fontWeight: 700, ...(ROLE_ST[u.role] || { background: '#eceff1', color: '#455a64' }) }}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnlineDlg(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* دیالوگ انقضای ویژه + ارسال پیامک */}
      <Dialog open={expDlg} onClose={() => setExpDlg(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ fontWeight: 'bold' }}>
          ⏳ انقضای اشتراک ویژه در {userStats.premium_expiring_month_fa || 'ماه جاری'} ({toFarsiDigits(expRows.length)} نفر)
        </DialogTitle>
        <DialogContent>
          {expRows.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              کاربر ویژه‌ای در {userStats.premium_expiring_month_fa || 'ماه جاری'} منقضی نمی‌شود.
            </Typography>
          )}
          {expRows.map((u) => (
            <Box key={u.id} display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '7px 0', borderBottom: '1px solid #f0f1f8', fontSize: 13, flexWrap: 'wrap', gap: 8 }}>
              <Box style={{ minWidth: 160 }}>
                <div style={{ fontWeight: 700 }}>{u.name || u.username}</div>
                <div style={{ fontFamily: 'monospace', direction: 'ltr', color: '#9aa1b9', fontSize: 11 }}>{u.username}</div>
              </Box>
              <Chip size="small" label={`${toFarsiDigits(u.contracts_count ?? 0)} قرارداد`}
                style={{ minWidth: 92, fontWeight: 700, background: '#e8eaf6', color: '#283593' }} />
              <Chip size="small" label={`${toFarsiDigits(u.transfers_year ?? 0)} حواله در یکسال گذشته`}
                style={{ minWidth: 172, fontWeight: 700, background: '#e0f2f1', color: '#00695c' }} />
              <span dir="ltr" style={{ color: '#666' }}>{u.mobile ? '0' + u.mobile : '—'}</span>
              <span style={{ color: '#d84315', fontWeight: 700 }}>انقضا: {toFarsiDigits(u.expiry_fa)}</span>
            </Box>
          ))}
          {smsResult && (
            <MuiAlert severity={smsResult.sent >= 0 ? 'success' : 'error'} style={{ marginTop: 12 }}>
              {smsResult.sent >= 0
                ? `ارسال شد: ${toFarsiDigits(smsResult.sent)} | ناموفق: ${toFarsiDigits(smsResult.failed)} | بدون موبایل: ${toFarsiDigits(smsResult.skipped_no_mobile)} | تکراری(رها شده): ${toFarsiDigits(smsResult.duplicates_skipped)} | کل: ${toFarsiDigits(smsResult.total)}`
                : 'خطا در ارسال پیامک'}
            </MuiAlert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpDlg(false)}>بستن</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={smsSending || expRows.length === 0}
            onClick={sendExpirySms}
            style={{ fontWeight: 'bold' }}
          >
            {smsSending ? 'در حال ارسال...' : '📱 ارسال پیامک انقضا'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* دیالوگ پرداخت‌های تأییدشدهٔ ثبت‌نشده */}
      <Dialog open={payDlg} onClose={() => setPayDlg(false)} fullWidth maxWidth="md" dir="rtl">
        <DialogTitle style={{ fontWeight: 'bold' }}>
          ⚠️ پرداخت‌های تأییدشده که اشتراک‌شان ثبت نشده ({toFarsiDigits(payAlerts.count)})
        </DialogTitle>
        <DialogContent>
          <MuiAlert severity="info" style={{ marginBottom: 12, direction: 'rtl' }}>
            این پرداخت‌ها در درگاه <b>تأیید شده‌اند</b> (پول از حساب کاربر کم شده) اما اشتراک ثبت نشده است.
            برای رفع، اشتراک کاربر را از «افزودن اشتراک دستی» فعال کنید یا با پذیرندگی درگاه بررسی کنید.
          </MuiAlert>
          {payAlerts.items.length === 0 && (
            <Typography variant="body2" color="textSecondary">موردی نیست.</Typography>
          )}
          {payAlerts.items.map((a) => (
            <Box key={a.id} display="flex" justifyContent="space-between" alignItems="center"
              style={{ padding: '8px 0', borderBottom: '1px solid #f0f1f8', fontSize: 13 }}>
              <Box style={{ minWidth: 170 }}>
                <div style={{ fontWeight: 700 }}>{a.name}</div>
                <div style={{ color: '#9aa1b9', fontSize: 11 }}>{a.created_jalali}</div>
              </Box>
              <Chip size="small" label={`${toFarsiDigits(a.contracts_count ?? 0)} قرارداد`}
                style={{ minWidth: 92, fontWeight: 700, background: '#e8eaf6', color: '#283593' }} />
              <Chip size="small" label={`${toFarsiDigits(a.transfers_year ?? 0)} حواله در یکسال گذشته`}
                style={{ minWidth: 172, fontWeight: 700, background: '#e0f2f1', color: '#00695c' }} />
              <span dir="ltr" style={{ color: '#666' }}>{a.mobile ? '0' + a.mobile : '—'}</span>
              <span style={{ fontWeight: 700 }}>
                {toFarsiDigits(Number(a.amount).toLocaleString('fa-IR'))} ریال
              </span>
              <span style={{ color: '#9aa1b9' }}>{toFarsiDigits(a.years)} ساله</span>
              <Chip size="small" label={a.state_label}
                style={{ background: '#fdecea', color: '#b71c1c' }} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDlg(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* دیالوگ بازدیدکنندگان (آنلاین / امروز / این ماه) */}
      <Dialog open={visDlg} onClose={() => setVisDlg(false)} fullWidth maxWidth="md" dir="rtl">
        <DialogTitle style={{ fontWeight: 'bold' }}>
          👥 {visTitle} ({toFarsiDigits(visRows.length)})
        </DialogTitle>
        <DialogContent>
          {visErr && (
            <Typography variant="body2" style={{ color: '#c62828', fontWeight: 700, marginBottom: 8 }}>
              {visErr}
            </Typography>
          )}
          {!visErr && visRows.length === 0 && (
            <Typography variant="body2" color="textSecondary">موردی نیست.</Typography>
          )}
          {visRows.map((u) => (
            <Box key={u.id} display="flex" justifyContent="space-between" alignItems="center"
              style={{ padding: '8px 0', borderBottom: '1px solid #f0f1f8', fontSize: 13 }}>
              <Box style={{ minWidth: 170 }}>
                <div style={{ fontWeight: 700 }}>{u.name}</div>
                <div style={{ color: '#9aa1b9', fontSize: 11 }}>{u.username}</div>
              </Box>
              {/* سازمان/مدیر سازمان: قرارداد و حواله و اشتراک ندارند ⇒ این آمار نمایش داده نمی‌شود */}
              {isContractorRole(u.role) && (
                <>
                  <Chip size="small" label={`${toFarsiDigits(u.contracts_count ?? 0)} قرارداد`}
                    style={{ minWidth: 92, fontWeight: 700, background: '#e8eaf6', color: '#283593' }} />
                  <Chip size="small" label={`${toFarsiDigits(u.transfers_year ?? 0)} حواله در یکسال گذشته`}
                    style={{ minWidth: 172, fontWeight: 700, background: '#e0f2f1', color: '#00695c' }} />
                </>
              )}
              {isContractorRole(u.role) && !u.subscribed && (
                <Chip size="small"
                  label={`${toFarsiDigits(u.blocked_today ?? 0)} تلاش ناموفق`}
                  title={`این ماه: ${toFarsiDigits(u.blocked_month ?? 0)}`}
                  style={{ minWidth: 126, fontWeight: 700, background: '#fdecea', color: '#b71c1c' }} />
              )}
              <Chip size="small" label={ROLE_LB[u.role] || u.role_fa || u.role}
                style={{ background: '#e8eafd', color: '#1a237e' }} />
              {isContractorRole(u.role) && (
                <Chip size="small"
                  label={u.subscribed ? '⭐ ویژه' : 'بدون اشتراک'}
                  style={u.subscribed
                    ? { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', fontWeight: 700 }
                    : { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', fontWeight: 700 }} />
              )}
              <span style={{ fontWeight: 700 }}>{toFarsiDigits(u.visits)} بازدید</span>
              <span dir="ltr" style={{ color: '#666' }}>{u.mobile ? '0' + u.mobile : '—'}</span>
              {/* ورود و خروج */}
              <span style={{ color: '#2e7d32', fontSize: 11, whiteSpace: 'nowrap' }}>
                ورود: {u.entered_at_fa || '—'}
              </span>
              {u.ip ? (
                <span dir="ltr" title="IP و دستگاهِ این بازدید"
                  style={{ color: '#546e7a', fontSize: 11, whiteSpace: 'nowrap', background: '#eceff1',
                    borderRadius: 6, padding: '1px 6px' }}>
                  {u.ip}{u.device ? ` · ${u.device}` : ''}
                </span>
              ) : null}
              <span style={{ color: '#c62828', fontSize: 11, whiteSpace: 'nowrap' }}>
                خروج: {u.exited_at_fa || '—'}
              </span>
              <span style={{ color: '#9aa1b9', fontSize: 11 }}>{u.last_seen_fa || '—'}</span>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisDlg(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* مرکز پیام مدیریت: صندوق + نظارت */}
      <Dialog open={msgDlg} onClose={() => setMsgDlg(false)} fullWidth maxWidth="md" dir="rtl">
        <DialogTitle style={{ fontWeight: 'bold' }}>💬 مرکز پیام مدیریت</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} style={{ marginBottom: 12 }}>
            <Button variant={msgScope === 'inbox' ? 'contained' : 'outlined'} color="primary"
              onClick={() => openMsgs('inbox')}>صندوق من</Button>
            <Button variant={msgScope === 'oversight' ? 'contained' : 'outlined'} color="primary"
              onClick={() => openMsgs('oversight')}>نظارت بر همهٔ پیام‌ها</Button>
          </Box>
          {msgScope === 'oversight' && (
            <MuiAlert severity="info" style={{ marginBottom: 10, direction: 'rtl' }}>
              حالت نظارت: همهٔ گفتگوهای سامانه — فقط خواندنی.
            </MuiAlert>
          )}
          {msgRows.length === 0 && (
            <Typography variant="body2" color="textSecondary">موردی نیست.</Typography>
          )}
          {msgRows.map((r) => (
            <Box key={`${r.contract_id}-${r.subject}-${r.sender_id}-${r.receiver_id}`}
              style={{ padding: '10px 0', borderBottom: '1px solid #f0f1f8', fontSize: 13 }}>
              <div style={{ fontWeight: 700 }}>
                {msgScope === 'inbox'
                  ? (r.other_user_name || '—')
                  : `${r.sender_name} ↔ ${r.receiver_name}`}
                {r.unread_count > 0 && (
                  <Chip size="small" label={`${toFarsiDigits(r.unread_count)} نخوانده`}
                    style={{ marginRight: 8, background: '#fdecea', color: '#b71c1c' }} />
                )}
              </div>
              <div style={{ color: '#78909c', fontSize: 12 }}>
                سرفصل: {r.subject}
                {r.contract_number ? ` | پروژه: ${r.contract_number}` : ' | (بدون پروژه)'}
              </div>
              <div style={{ color: '#546e7a', marginTop: 2 }}>{r.last_message}</div>
              <div style={{ color: '#9aa1b9', fontSize: 11 }}>
                {gregorianToJalali(r.last_message_datetime)}
              </div>
              {msgScope === 'inbox' && r.other_user_id && (
                <Box display="flex" gap={1} style={{ marginTop: 8 }}>
                  <TextField size="small" variant="outlined" fullWidth placeholder="پاسخ..."
                    value={msgText[r.other_user_id] || ''}
                    onChange={(e) => setMsgText((m) => ({ ...m, [r.other_user_id]: e.target.value }))} />
                  <Button variant="contained" color="primary" disabled={msgBusy}
                    onClick={() => sendReply(r)}>ارسال</Button>
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsgDlg(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default AdminDashboard;
